import styles from './GameHistory.module.scss';
import { useQuery } from 'react-query';
import { getUserRecentGames, IRecentGame } from '../../../../api/user';
import { API_URL, CURRENCY, QueryKeys } from '../../../../constants';

interface IGameHistoryProps {
    userId: string;
}

// The API serves a square picture per game version under a lowercased slug. A background image (not
// an <img>) so a version without artwork degrades to an empty tile instead of a broken-image icon.
const getGameIconUrl = (game: IRecentGame) =>
    `${API_URL}/api/game-pictures/${game.gameVersion.toLowerCase()}/square.png`;

// Mode artwork is served under the exact enum name (lowercasing it 404s) and is layered over the
// game picture.
const getGameModeIconUrl = (game: IRecentGame) =>
    `${API_URL}/api/game-modes/${game.mode}.png`;

// What is left of the row title once the game and its mode are shown as icons.
const getGameDetails = (game: IRecentGame) => {
    const details = [
        game.isTournament ? 'tournament' : null,
        game.opponentNickName ? `vs ${game.opponentNickName}` : null,
    ].filter(Boolean);

    return details.length > 0 ? details.join(' · ') : game.game;
};

// The icons carry the game, version and mode, so the exact names stay reachable on hover.
const getGameTitle = (game: IRecentGame) =>
    [`${game.game} (${game.gameVersion})`, game.mode].filter(Boolean).join(' · ');

const getResultView = (game: IRecentGame): { text: string; className: string } => {
    switch (game.result) {
        case 'Win':
            return { text: 'Win', className: [styles.result, styles.win].join(' ') };
        case 'Loss':
            return { text: 'Loss', className: [styles.result, styles.loss].join(' ') };
        default:
            return { text: game.result, className: [styles.result, styles.draw].join(' ') };
    }
};

// A brief game history for withdrawal review: an account that only deposits and withdraws without
// real play stands out immediately by the lifetime counter and an empty list.
export const GameHistory = ({ userId }: IGameHistoryProps) => {
    const { data, isLoading } = useQuery([QueryKeys.getUserRecentGames, userId], () => getUserRecentGames(userId), {
        enabled: !!userId,
    });

    if (isLoading || !data) return null;

    return (
        <div className={styles.container}>
            <div className={styles.title}>Game History</div>
            <div className={styles.subTitle}>{data.totalGames} games played in total</div>
            <div className={styles.block}>
                {data.games.length === 0 && <div className={styles.empty}>No finished games</div>}
                {data.games.map((game) => {
                    const result = getResultView(game);
                    return (
                        <div className={styles.row} key={`${game.challengeId}-${game.finishedAt}`}>
                            <div
                                className={styles.gameIcon}
                                style={{ backgroundImage: `url(${getGameIconUrl(game)})` }}
                                role="img"
                                aria-label={getGameTitle(game)}
                                title={getGameTitle(game)}
                            >
                                {game.mode && (
                                    <div
                                        className={styles.gameModeIcon}
                                        style={{ backgroundImage: `url(${getGameModeIconUrl(game)})` }}
                                    />
                                )}
                            </div>
                            <div className={styles.column}>
                                <div className={styles.name}>{getGameDetails(game)}</div>
                                <div className={styles.time}>{new Date(game.finishedAt).toLocaleString()}</div>
                            </div>
                            <div className={result.className}>
                                {result.text}
                                <div className={styles.amount}>
                                    {game.result === 'Win' ? `+${game.amount}` : `bet ${game.bet}`}
                                    {CURRENCY}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
